import { Box, useTheme } from "@mui/material";
import { FC } from 'react';
import CollapsibleSection from './EventBrowser/CollapsibleSection';
import EventFilterField from './EventBrowser/EventFilterField';
import EventList from './EventBrowser/EventList';
import OlderEventsYearGroup from './EventBrowser/OlderEventsYearGroup';
import { useEventBrowser } from './EventBrowser/useEventBrowser';
import ExportButton from './components/ExportButton';
import { useExportManager } from './hooks/useExportManager';

/**
 * Main EventBrowser component - displays events organized by time periods
 * with filtering, collapsible sections, and album export
 */
const EventBrowser: FC<{}> = () => {
  const theme = useTheme();
  const {
    eventsByTimePeriod,
    sectionsExpanded,
    toggleSection,
    handleFilterChange,
    useCollapsibleSections,
    eventsByYear,
  } = useEventBrowser();

  const [exportState, exportActions] = useExportManager();

  return (
    <Box sx={{
      maxWidth: '100%',
      overflow: 'hidden',
      width: '100%'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ExportButton exportState={exportState} exportActions={exportActions} />
      </Box>

      <EventFilterField onFilterChange={handleFilterChange} />

      <Box sx={{
        maxWidth: '100%',
        overflow: 'hidden',
        '& .MuiTreeItem-root': {
          color: theme.palette.text.primary,
        },
        '& .MuiTreeItem-label': {
          color: theme.palette.text.primary,
          maxWidth: '100%',
          overflow: 'hidden',
        },
        '& .MuiTreeItem-iconContainer': {
          color: theme.palette.text.secondary,
        },
        '& .MuiTreeView-root': {
          maxWidth: '100%',
          overflow: 'hidden',
        },
      }}>
        {useCollapsibleSections ? (
          <>
            {/* Recent Events (Last 90 days) */}
            {eventsByTimePeriod.recent.length > 0 && (
              <CollapsibleSection
                title="Recent"
                isExpanded={sectionsExpanded.recent}
                onToggle={() => toggleSection('recent')}
              >
                <EventList
                  events={eventsByTimePeriod.recent}
                  keyPrefix="recent"
                  selectedEventIds={exportState.selectedEventIds}
                  onSelectionChange={exportActions.toggleEventSelection}
                />
              </CollapsibleSection>
            )}

            {/* Last Year Events */}
            {eventsByTimePeriod.lastYear.length > 0 && (
              <CollapsibleSection
                title="Last year"
                isExpanded={sectionsExpanded.lastYear}
                onToggle={() => toggleSection('lastYear')}
              >
                <EventList
                  events={eventsByTimePeriod.lastYear}
                  keyPrefix="lastyear"
                  selectedEventIds={exportState.selectedEventIds}
                  onSelectionChange={exportActions.toggleEventSelection}
                />
              </CollapsibleSection>
            )}

            {/* Older Events (grouped by year) */}
            {Object.keys(eventsByTimePeriod.older).length > 0 && (
              <CollapsibleSection
                title="Older"
                isExpanded={sectionsExpanded.older}
                onToggle={() => toggleSection('older')}
              >
                {Object.entries(eventsByTimePeriod.older)
                  .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
                  .map(([year, yearEvents]) => (
                    <OlderEventsYearGroup
                      key={`older-year-${year}`}
                      year={year}
                      events={yearEvents}
                      selectedEventIds={exportState.selectedEventIds}
                      onSelectionChange={exportActions.toggleEventSelection}
                    />
                  ))}
              </CollapsibleSection>
            )}
          </>
        ) : (
          <>
            {Object.entries(eventsByYear)
              .sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
              .map(([year, yearEvents]) => (
                <OlderEventsYearGroup
                  key={`year-${year}`}
                  year={year}
                  events={yearEvents}
                  selectedEventIds={exportState.selectedEventIds}
                  onSelectionChange={exportActions.toggleEventSelection}
                />
              ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default EventBrowser;
